import { format, isAfter } from 'date-fns'
import React, { FC, RefObject, useMemo, useRef } from 'react'
import {
  ATTACHMENT_TYPE,
  MEDIA_DATA_TYPE,
  POST_SOCIAL_STATUS,
  POST_TYPE,
  SOCIAL_NETWORK
} from '../../../../models/post'
import { getPostFromMamagePost } from '../../../../selectors/PostSelector'
import { TSocialDetails } from '../../../../selectors/ProjectSelector'
import { useSelector } from '../../../../store'
import { TLatestPost, TOriginalIntagramPost } from '../../../../store/gridView/types'
import {
  createEmptyAttachment,
  createEmptyPostSocialsBySelectedSocials,
  TExtendedPostAttachment,
  TLatestSchedule,
  TPostSocialManagePost
} from '../../../../store/managePost/newTypes'
import { TPost, TPostSocial } from '../../../../store/managePost/types'
import FbImageLibrary from '../../../FbImageLibrary'
import { IMAGE_GRID_TYPE, TGridImage } from '../../../FbImageLibrary/components/Images'
import Ellipsis from '../../../UI/Icons/Ellipsis'

import './GridPostsInstagram.scss'

type TMinimumPostToRender = {
  latest_schedule: TLatestSchedule
  post_socials: TPostSocialManagePost[]
  isMokup: boolean
  id: number
}

const transformInstaPostToOriginal = (post: TOriginalIntagramPost): TMinimumPostToRender => {
  const postSocials = createEmptyPostSocialsBySelectedSocials(
    [SOCIAL_NETWORK.INSTAGRAM_SOCIAL],
    POST_TYPE.POST_TYPE_SINGLE_PHOTO
  )
  const pathToMedia = post.thumbnail_url ? post.thumbnail_url : post.media_url
  const postSocialsWithAttachment = postSocials.map((socialPost) => ({
    ...socialPost,
    attachments: [createEmptyAttachment(pathToMedia)]
  }))

  const dateWithoutTimezone = post.created_at.split('+')[0]
  const date = new Date(dateWithoutTimezone)

  return {
    post_socials: postSocialsWithAttachment,
    latest_schedule: {
      id: 0,
      is_autoposting: false,
      timezone: '',
      date: format(date, 'yyyy-MM-dd'),
      time: format(date, 'hh:mm'),
      datetime: ''
    },
    id: 0,
    isMokup: true
  }
}

interface InstagramPreviewProps {
  socialDetails: TSocialDetails
  posts: TLatestPost[]
}

const InstagramGrid: FC<InstagramPreviewProps> = ({ socialDetails, posts }) => {
  const { currentPost } = useSelector((state) => ({
    currentPost: getPostFromMamagePost(state)
  }))
  const socialData = useMemo(() => socialDetails[SOCIAL_NETWORK.INSTAGRAM_SOCIAL], [])

  const latestPostsWithNewOne = useMemo(() => {
    const postsCopy = posts.map(({ post, instagramMedia }) =>
      post ? post : transformInstaPostToOriginal(instagramMedia!)
    )
    const postIndex = postsCopy.findIndex(
      (post) => !(post as any).isMokup && post.id === currentPost.id
    )

    if (!!currentPost.id && postIndex !== -1) {
      postsCopy.splice(postIndex, 1, currentPost)
    } else {
      postsCopy.unshift(currentPost)
    }

    return postsCopy.sort((postA, postB) => {
      const { date: postDateA, time: postTimeA } = postA.latest_schedule
      const { date: postDateB, time: postTimeB } = postB.latest_schedule

      return isAfter(new Date(`${postDateA}T${postTimeA}`), new Date(`${postDateB}T${postTimeB}`))
        ? -1
        : 1
    })
  }, [currentPost, posts])

  const instaGridImages = useMemo(() => {
    let gridImages: TGridImage[] = []

    latestPostsWithNewOne.forEach((post) => {
      let mediaDataToGrid

      for (let socialPost of post.post_socials) {
        if (socialPost.social_network === SOCIAL_NETWORK.INSTAGRAM_SOCIAL) {
          mediaDataToGrid = socialPost.attachments[0]?.object_data
          break
        }
      }

      const { date, time, is_autoposting } = post.latest_schedule
      const socialPost = post.post_socials.find(
        (post) => post.social_network === SOCIAL_NETWORK.INSTAGRAM_SOCIAL
      )!

      if (mediaDataToGrid?.public_url) {
        let public_url = mediaDataToGrid.public_url

        if (mediaDataToGrid.options.type === ATTACHMENT_TYPE.TYPE_VIDEO) {
          const videoPreviews = mediaDataToGrid.previews
          public_url =
            videoPreviews?.original || videoPreviews?.medium || videoPreviews?.small || ''
        }

        gridImages.push({
          ...mediaDataToGrid,
          public_url,
          dateData: {
            date: format(new Date(`${date}T${time}`), 'dd MMM yyyy KK:mm aa'),
            isAutoposting: is_autoposting,
            status: socialPost.status || POST_SOCIAL_STATUS.STATUS_DRAFT,
            isMokup: !!(post as any).isMokup
          }
        })
      } else {
        gridImages.push({
          content_type: '',
          file_path: '',
          file_size: 0,
          id: 0,
          options: {
            height: 0,
            ratio: '',
            type: ATTACHMENT_TYPE.TYPE_IMAGE,
            width: 0
          },
          is_temporary: true,
          media_collection_type: '',
          parent_media_id: 0,
          original_name: '',
          public_url: '',
          user_id: 0,
          canva_design_id: null,
          dateData: {
            date: format(new Date(`${date}T${time}`), 'dd MMM yyyy KK:mm aa'),
            isAutoposting: is_autoposting,
            status: socialPost.status || POST_SOCIAL_STATUS.STATUS_DRAFT,
            isMokup: !!(post as any).isMokup
          }
        })
      }
    })

    return gridImages
  }, [latestPostsWithNewOne])

  return (
    <div className={`instagram-grid post-grid`}>
      <div className='instagram-grid-customer'>
        <div className='instagram-grid-customer__avatar'>
          {socialData.logo.original ? (
            <img
              src={socialData.logo.original}
              alt='any'
              className='instagram-grid-customer__avatar__img'
            />
          ) : (
            socialData.logo.default
          )}
        </div>
        <div className='instagram-grid-customer__data'>
          <div className='instagram-grid-customer__data__name'>{socialData.name}</div>
        </div>
        <div className='instagram-grid-customer__etc'>
          <Ellipsis />
        </div>
      </div>

      <div className='instagram-grid-previews'>
        <FbImageLibrary
          hideOverlay={true}
          isEditable={false}
          countFrom={9}
          gridType={IMAGE_GRID_TYPE.INSTA_GRID}
          images={instaGridImages} // object_data
        />
      </div>
    </div>
  )
}

export default InstagramGrid
